/**
 * lib/teacherAssignments.ts
 * ------------------------------------------------------------------
 * Round 5: single source of truth for class-teacher / assistant
 * class-teacher writes.
 *
 * The data model carries this relationship redundantly:
 *
 *   Teacher.classTeacherOf            (string | '')      classId
 *   Teacher.isClassTeacher            (boolean)
 *   Teacher.assistantClassTeacherOf   (string[])         classIds
 *   Teacher.isAssistantClassTeacher   (boolean)
 *
 *   SchoolClass.classTeacherId            (string | null) teacherId
 *   SchoolClass.assistantClassTeacherId   (string | null) teacherId
 *
 * Every edit must update BOTH sides or the system drifts. Permissions
 * read from the Teacher side; ClassesSetup reads from the Class side;
 * the demo data shows both being populated. We write to both.
 *
 * These helpers do the atomic update — call them from any UI that
 * changes a class-teacher or assistant-class-teacher relationship.
 * ------------------------------------------------------------------
 */
import { doc, writeBatch } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/contexts/DataContext';
import type { Teacher, SchoolClass } from '@/types';

/* ==================================================================
   CLASS TEACHER
   ================================================================== */

/**
 * Set (or clear) a class's class teacher.
 *
 * Atomically:
 *   - Updates SchoolClass.classTeacherId
 *   - Sets Teacher.classTeacherOf = classId, isClassTeacher = true
 *     on the new teacher (if any)
 *   - Clears Teacher.classTeacherOf and isClassTeacher = false on
 *     the OLD teacher (if there was one)
 *   - If the new teacher was already class teacher of a DIFFERENT
 *     class, that other class's classTeacherId is cleared too
 *     (each teacher can only be class teacher of one class)
 *
 * @param classId      the class to update
 * @param newTeacherId the teacher to assign, or '' / null to clear
 * @param ctx          ambient data needed for the cross-doc update
 * @param actorUid     who's making the change (for updatedBy)
 */
export async function setClassTeacher(
  classId: string,
  newTeacherId: string | null,
  ctx: { classes: SchoolClass[]; teachers: Teacher[] },
  actorUid: string | undefined,
): Promise<void> {
  const batch = writeBatch(db);
  const now = new Date().toISOString();

  const targetClass = ctx.classes.find((c) => c.id === classId);
  if (!targetClass) throw new Error(`Class ${classId} not found`);

  const oldTeacherId = targetClass.classTeacherId ?? null;
  // Same as before? Nothing to do.
  if ((newTeacherId ?? null) === oldTeacherId) return;

  // 1. Update the class doc
  batch.set(
    doc(db, COLLECTIONS.classes, classId),
    {
      classTeacherId: newTeacherId || null,
      updatedAt: now,
      updatedBy: actorUid,
    },
    { merge: true },
  );

  // 2. Clear the previous class-teacher's flag (if any and changing)
  if (oldTeacherId && oldTeacherId !== newTeacherId) {
    batch.set(
      doc(db, COLLECTIONS.teachers, oldTeacherId),
      {
        classTeacherOf: '',
        isClassTeacher: false,
        updatedAt: now,
        updatedBy: actorUid,
      },
      { merge: true },
    );
  }

  // 3. Set the new class-teacher's flag (if there is one)
  if (newTeacherId) {
    const newTeacher = ctx.teachers.find((t) => t.id === newTeacherId);
    // 3a. If the new teacher was already class teacher of a DIFFERENT
    //     class, clear that other class first (1:1 invariant).
    if (
      newTeacher?.classTeacherOf &&
      newTeacher.classTeacherOf !== classId
    ) {
      batch.set(
        doc(db, COLLECTIONS.classes, newTeacher.classTeacherOf),
        {
          classTeacherId: null,
          updatedAt: now,
          updatedBy: actorUid,
        },
        { merge: true },
      );
    }
    batch.set(
      doc(db, COLLECTIONS.teachers, newTeacherId),
      {
        classTeacherOf: classId,
        isClassTeacher: true,
        updatedAt: now,
        updatedBy: actorUid,
      },
      { merge: true },
    );
  }

  await batch.commit();
}


/* ==================================================================
   ASSISTANT CLASS TEACHER
   ================================================================== */

/**
 * Set (or clear) a class's assistant class teacher.
 *
 * Atomically:
 *   - Updates SchoolClass.assistantClassTeacherId
 *   - On the new teacher: adds classId to assistantClassTeacherOf[]
 *     and sets isAssistantClassTeacher = true
 *   - On the OLD teacher: removes classId from assistantClassTeacherOf[]
 *     and (if that empties the array) sets isAssistantClassTeacher = false
 *
 * Unlike the primary class-teacher role, a single teacher CAN be
 * assistant class teacher of MULTIPLE classes — the array shape
 * supports it. We don't force a 1:1 invariant here.
 */
export async function setAssistantClassTeacher(
  classId: string,
  newTeacherId: string | null,
  ctx: { classes: SchoolClass[]; teachers: Teacher[] },
  actorUid: string | undefined,
): Promise<void> {
  const batch = writeBatch(db);
  const now = new Date().toISOString();

  const targetClass = ctx.classes.find((c) => c.id === classId);
  if (!targetClass) throw new Error(`Class ${classId} not found`);

  const oldTeacherId = targetClass.assistantClassTeacherId ?? null;
  if ((newTeacherId ?? null) === oldTeacherId) return;

  // 1. Class doc
  batch.set(
    doc(db, COLLECTIONS.classes, classId),
    {
      assistantClassTeacherId: newTeacherId || null,
      updatedAt: now,
      updatedBy: actorUid,
    },
    { merge: true },
  );

  // 2. Old teacher: remove this classId from assistantClassTeacherOf
  if (oldTeacherId && oldTeacherId !== newTeacherId) {
    const oldT = ctx.teachers.find((t) => t.id === oldTeacherId);
    const remaining = (oldT?.assistantClassTeacherOf ?? []).filter(
      (cid) => cid !== classId,
    );
    batch.set(
      doc(db, COLLECTIONS.teachers, oldTeacherId),
      {
        assistantClassTeacherOf: remaining,
        isAssistantClassTeacher: remaining.length > 0,
        updatedAt: now,
        updatedBy: actorUid,
      },
      { merge: true },
    );
  }

  // 3. New teacher: add this classId to assistantClassTeacherOf
  if (newTeacherId) {
    const newT = ctx.teachers.find((t) => t.id === newTeacherId);
    const existing = newT?.assistantClassTeacherOf ?? [];
    const next = existing.includes(classId) ? existing : [...existing, classId];
    batch.set(
      doc(db, COLLECTIONS.teachers, newTeacherId),
      {
        assistantClassTeacherOf: next,
        isAssistantClassTeacher: true,
        updatedAt: now,
        updatedBy: actorUid,
      },
      { merge: true },
    );
  }

  await batch.commit();
}


/* ==================================================================
   TEACHING ASSIGNMENTS (subject → classes)
   ==================================================================
   These live ONLY on the Teacher doc, so no two-sided sync. But
   teachingAssignments (form-level) is a derived fallback view of
   classAssignments (precise) — we keep that derivation in one place.
   ================================================================== */

/**
 * Given a teacher's classAssignments (subject -> classIds), derive
 * the form-level teachingAssignments (form -> subjectIds) by looking
 * up each class's form.
 */
export function deriveTeachingAssignments(
  classAssignments: Record<string, string[]>,
  classes: SchoolClass[],
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const [subjectId, classIds] of Object.entries(classAssignments)) {
    for (const cid of classIds) {
      const cls = classes.find((c) => c.id === cid);
      if (!cls?.form) continue;
      const form = String(cls.form);
      if (!out[form]) out[form] = [];
      if (!out[form].includes(subjectId)) out[form].push(subjectId);
    }
  }
  return out;
}

/**
 * Atomically save a teacher's full teaching-assignment map.
 * (Does NOT touch class-teacher fields — use setClassTeacher /
 *  setAssistantClassTeacher for those.)
 */
export async function saveTeachingAssignments(
  teacherId: string,
  classAssignments: Record<string, string[]>,
  ctx: { classes: SchoolClass[] },
  actorUid: string | undefined,
): Promise<void> {
  // Strip empty subject entries
  const clean: Record<string, string[]> = {};
  for (const [sid, clsIds] of Object.entries(classAssignments)) {
    if (clsIds.length > 0) clean[sid] = clsIds;
  }
  const teachingAssignments = deriveTeachingAssignments(clean, ctx.classes);

  const batch = writeBatch(db);
  batch.set(
    doc(db, COLLECTIONS.teachers, teacherId),
    {
      classAssignments: clean,
      teachingAssignments,
      updatedAt: new Date().toISOString(),
      updatedBy: actorUid,
    },
    { merge: true },
  );
  await batch.commit();
}
